import './NumberInput.less';
import React from 'react';
import Icon from '../icon/Icon';
import {getLocale} from "../../i18n";
import {regexEscapeString} from "../../utils/regex";
import {addMouseWheelListener, removeMouseWheelListener} from "../../dom/event-handlers/on-mouse-wheel";

const locale = getLocale();
const isCommaStyle = locale === 'vi';

const MAX_DIGITS = 15;
const CH_DOT = '.';
const CH_COMMA = ',';
const CH_MINUS = '-';

// -- closed style --
const COMMA_DEC = '' + CH_COMMA + '';
const DOT_DEC = '' + CH_DOT + '';
// -- padding style --
// const COMMA_DEC = ' ' + CH_COMMA + ' ';
// const DOT_DEC = ' ' + CH_DOT + ' ';

const COMMA_THO = CH_COMMA;
const DOT_THO = CH_DOT;

const DEC_SEP = isCommaStyle ? COMMA_DEC : DOT_DEC;
const THO_SEP = isCommaStyle ? DOT_THO : COMMA_THO;

// -- closed style --
const SIGN = CH_MINUS + '';
// -- padding style --
// const SIGN = CH_MINUS + ' ';

// -- closed style --
const DEC_PARSING_PATTERN = (isCommaStyle ? regexEscapeString(COMMA_DEC) : regexEscapeString(DOT_DEC));
const DEC_TRIGGER_PATTERN = '^' + (isCommaStyle ? regexEscapeString(CH_COMMA) : regexEscapeString(CH_DOT)) + '$';
// -- padding style --
// const DEC_PARSING_PATTERN = regexEscapeString(COMMA_DEC) + '|' + regexEscapeString(DOT_DEC);
// const DEC_TRIGGER_PATTERN = '^' + regexEscapeString(CH_COMMA) + '|' + regexEscapeString(CH_DOT) + '$';

const SIG_PARSING_PATTERN = regexEscapeString(SIGN);
const SIG_TRIGGER_PATTERN = '^' + regexEscapeString(CH_MINUS) + '$';

const FUNC_KEY_PATTERN = '^Tab|Shift|Control|Alt|Enter|ArrowLeft|ArrowRight|Home|End|F[0-9]$';

export default class NumberInput extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            value: normalizeValue(this.props.defaultValue),
            focused: false
        };
    }

    componentDidUpdate() {
        if (this.props.value !== undefined) {
            const propValue = normalizeValue(this.props.value);

            if (propValue !== this.state.value) {
                this.inputEl.value = inputValueFromNumber(propValue);
                this.setValue(this.props.value);
            }
        }
    }

    setValue = (value) => {
        const {onChange} = this.props;

        this.setState(
            {value: normalizeValue(value)},
            () => {
                onChange && onChange(this.state.value);
            }
        );
    };

    onFocus = () => {
        this.setState({
            focused: true
        });
    };

    onBlur = (ev) => {
        const {min, max} = this.props;
        const {value} = this.state;

        if (value !== null && (min !== undefined || max !== undefined)) {
            if (min !== undefined && value < min) {
                this.setValue(min);
                ev.target.value = inputValueFromNumber(min);
            }

            if (max !== undefined && value > max) {
                this.setValue(max);
                ev.target.value = inputValueFromNumber(max);
            }
        } else {
            ev.target.value = numberToString(stringToNumber(ev.target.value, true));
        }

        this.setState({focused: false});
    };

    onPaste = (ev) => {
        ev.preventDefault();

        for (let i = 0; i < ev.clipboardData.items.length; i++) {
            const item = ev.clipboardData.items[i];
            if (item.type === 'text/plain') {
                item.getAsString(pastedStr => {
                    const text = this.inputEl.value;
                    const selectionStart = this.inputEl.selectionStart;
                    const selectionEnd = this.inputEl.selectionEnd;

                    const [newNumber, newText, newSelectionStart] = handlePasteManually(pastedStr, text, selectionStart, selectionEnd);

                    this.inputEl.value = newText;
                    this.inputEl.selectionStart = this.inputEl.selectionEnd = newSelectionStart;
                    this.setValue(newNumber);
                });
                break;
            }
        }
    };

    onKeyDown = (ev) => {
        if (ev.ctrlKey || ev.altKey) {
            return;
        }

        if (isMatch(ev.key, FUNC_KEY_PATTERN)) {
            return;
        }

        ev.preventDefault();

        if (ev.key === 'ArrowUp') {
            this.stepUp();
            return;
        }

        if (ev.key === 'ArrowDown') {
            this.stepDown();
            return;
        }

        const key = ev.key;
        const text = ev.target.value;
        const selectionStart = ev.target.selectionStart;
        const selectionEnd = ev.target.selectionEnd;
        const before = text.substring(0, selectionStart);
        const behind = text.substring(selectionEnd, text.length);

        const args = [key, text, selectionStart, selectionEnd, before, behind];

        const newValues = measureNewValuesOnNumberKeys(...args) || measureNewValuesOnSpecialKeys(...args);

        if (newValues !== null) {
            const [newNumber, newText, newSelectionStart] = newValues;
            ev.target.value = newText;
            ev.target.selectionStart = ev.target.selectionEnd = newSelectionStart;
            this.setValue(newNumber);
        }
    };

    stepUp = () => {
        const {step, max} = this.props;
        const {value} = this.state;

        const maxDecimalDigits = Math.max(countDecimalDigits(step), countDecimalDigits(value));
        const K = Math.pow(10, maxDecimalDigits);

        let newNumber = Math.round(value * K + step * K) / K;
        if (max !== undefined) {
            newNumber = Math.min(max, newNumber);
        }

        const newText = numberToString(String(newNumber));

        this.inputEl.value = newText;
        this.setValue(newNumber);
    };

    stepDown = () => {
        const {step, min} = this.props;
        const {value} = this.state;

        const maxDecimalDigits = Math.max(countDecimalDigits(step), countDecimalDigits(value));
        const K = Math.pow(10, maxDecimalDigits);

        let newNumber = Math.round(value * K - step * K) / K;
        if (min !== undefined) {
            newNumber = Math.max(min, newNumber);
        }

        const newText = numberToString(String(newNumber));

        this.inputEl.value = newText;
        this.setValue(newNumber);
    };

    continuationTrigger = null;
    continuation = null;

    startContinuation = (act) => {
        act();

        this.continuationTrigger = setTimeout(() => {
            this.continuation = setInterval(act, 30);
        }, 300);
    };

    stopContinuation = () => {
        clearTimeout(this.continuationTrigger);
        clearInterval(this.continuation);
    };

    render() {
        const {disabled, unit, step, appearance = 'box'} = this.props;
        const {value, focused} = this.state;

        return <div
            className={
                `number-input appearance-${appearance}`
                + (focused ? ' focused' : '')
                + (disabled ? ' disabled' : '')
            }
            onClick={() => this.inputEl.focus()}
        >
            <div className="inner">
                <input
                    type="text"
                    onKeyUp={this.onKeyUp}
                    onKeyDown={this.onKeyDown}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    onPaste={this.onPaste}
                    defaultValue={inputValueFromNumber(value)}
                    disabled={disabled}
                    ref={el => this.inputEl = el}
                />

                {
                    unit &&
                    <span className="unit">{unit}</span>
                }

                {
                    step &&
                    <div className="controls">
                        <button
                            type="button"
                            className="step-up-button"
                            onMouseDown={() => this.startContinuation(this.stepUp)}
                            onMouseUp={() => this.stopContinuation()}
                            onMouseLeave={() => this.stopContinuation()}
                            tabIndex="-1"
                        >
                            <Icon name="angle-up"/>
                        </button>
                        <button
                            type="button"
                            className="step-down-button"
                            onMouseDown={() => this.startContinuation(this.stepDown)}
                            onMouseUp={() => this.stopContinuation()}
                            onMouseLeave={() => this.stopContinuation()}
                            tabIndex="-1"
                        >
                            <Icon name="angle-down"/>
                        </button>
                    </div>
                }
            </div>
        </div>;
    }
}

function countDecimalDigits(num) {
    const parts = String(num).split('.');

    if (parts.length < 2) {
        return 0;
    }

    return parts[1].length;
}

function normalizeValue(value) {
    return value === '' || value === null || isNaN(value) ? null : Number(value);
}

function inputValueFromNumber(value) {
    return numberToString(value === null ? '' : String(value));
}

function measureNewValuesOnNumberKeys(key, text, selectionStart, selectionEnd, before, behind) {
    if (!isMatch(key, /\d/)) {
        return null;
    }

    // ensure number string not exceed MAX_DIGITS
    // some cases cannot catch here will be trimmed on key up
    // however it will lead to unwanted "jumping" effect

    const isSelectedARange = selectionStart !== selectionEnd;

    const isBeforeSign = selectionStart === 0 && text.startsWith(SIGN);

    if (!isSelectedARange && (isBeforeSign || !canAddMoreDigits(text))) {
        return null;
    }

    const newBefore = before + key;
    const newNumber = stringToNumber(newBefore + behind);
    const newText = numberToString(newNumber);
    const newSelectionStart = selectionStart + getSelectionChange(newText, newBefore, before);

    return [newNumber, newText, newSelectionStart];
}

function measureNewValuesOnSpecialKeys(key, text, selectionStart, selectionEnd, before, behind) {
    if (key === 'Backspace') {
        let delLen = 1;
        if (selectionStart !== selectionEnd) {
            delLen = 0;
        } else if (before.endsWith(THO_SEP)) {
            delLen = 1 + THO_SEP.length;
        }
        const newBefore = before.substring(0, before.length - delLen);
        const newNumber = stringToNumber(newBefore + behind);
        const newText = numberToString(newNumber);
        const newSelectionStart = selectionStart + getSelectionChange(newText, newBefore, before);

        return [newNumber, newText, newSelectionStart];
    }

    if (key === 'Delete') {
        let delLen = 1;
        if (selectionStart !== selectionEnd) {
            delLen = 0;
        } else if (behind.startsWith(THO_SEP)) {
            delLen = 1 + THO_SEP.length;
        }
        const newBehind = behind.substring(delLen, behind.length);
        const newNumber = stringToNumber(before + newBehind);
        const newText = numberToString(newNumber);
        const newSelectionStart = selectionStart + getSelectionChange(newText, before, before);

        return [newNumber, newText, newSelectionStart];
    }

    if (isMatch(key, DEC_TRIGGER_PATTERN) && before !== '') {
        const newBefore = before.replace(new RegExp(DEC_PARSING_PATTERN, 'g'), '');
        const newBehind = behind.replace(new RegExp(DEC_PARSING_PATTERN, 'g'), '');
        const newNumber = stringToNumber(newBefore + DEC_SEP + newBehind);
        const newText = numberToString(newNumber);
        const newSelectionStart = newText.includes(DEC_SEP)
            ? newText.indexOf(DEC_SEP) + DEC_SEP.length
            : selectionStart;

        return [newNumber, newText, newSelectionStart];
    }

    if (isMatch(key, SIG_TRIGGER_PATTERN) && selectionStart === 0) {
        const newBehind = behind.replace(new RegExp(SIG_PARSING_PATTERN, 'g'), '');
        const newNumber = stringToNumber(SIGN + newBehind);
        const newText = numberToString(newNumber);
        const newSelectionStart = newText.includes(SIGN)
            ? newText.indexOf(SIGN) + SIGN.length
            : selectionStart;

        return [newNumber, newText, newSelectionStart];
    }

    return null;
}

function handlePasteManually(pastedStr, text, selectionStart, selectionEnd) {
    const before = text.substring(0, selectionStart);
    const behind = text.substring(selectionEnd, text.length);

    const newBefore = before + (isNaN(pastedStr) ? pastedStr : numberToString(pastedStr));
    const newNumber = stringToNumber(newBefore + behind);
    const newText = numberToString(newNumber);

    const selectionChange = getSelectionChange(newText, newBefore, before);
    const newSelectionStart = selectionStart + selectionChange;

    return [newNumber, newText, newSelectionStart];
}

function getSelectionChange(realNewText, tempNewBefore, realBefore) {
    let digitCount = 0;
    for (let i = 0; i < tempNewBefore.length; i++) {
        const char = tempNewBefore.charAt(i);
        const subtext = tempNewBefore.substring(i, tempNewBefore.length);
        if (isMatch(char, /\d/) || subtext.startsWith(DEC_SEP)) {
            digitCount++;
        }
    }

    let comparedDigitCount = 0;
    let realNewBeforeLength = tempNewBefore.length;
    for (let i = 0; i < realNewText.length; i++) {
        const char = realNewText.charAt(i);
        const subtext = realNewText.substring(i, realNewText.length);
        if (isMatch(char, /\d/) || subtext.startsWith(DEC_SEP)) {
            comparedDigitCount++;
        }

        if (comparedDigitCount === digitCount) {
            realNewBeforeLength = i + 1;
        } else if (comparedDigitCount > digitCount) {
            realNewBeforeLength = i;
            break;
        }
    }

    return realNewBeforeLength - realBefore.length;
}

function canAddMoreDigits(value) {
    const testStr = value + '1';
    return trimNumberString(testStr).length === testStr.length;
}

function isBetween(bridge, before, behind) {
    for (let i = 1; i < bridge.length; i++) {
        if (before.endsWith(bridge.substring(0, i)) && behind.startsWith(bridge.substring(i, bridge.length))) {
            return true;
        }
    }

    return false;
}

function isMatch(str, patternOrRegex) {
    return str.match(patternOrRegex instanceof RegExp ? patternOrRegex : new RegExp(patternOrRegex)) !== null;
}

function trimNumberString(numStr) {
    let str = '';
    let digitCount = 0;
    for (let i = 0; i < numStr.length && digitCount < MAX_DIGITS; i++) {
        const ch = numStr.charAt(i);
        str += ch;

        if (ch.match(/\d/) !== null) {
            digitCount++;
        }
    }

    return str;
}

function stringToNumber(numStr, strict = false) {
    let str = trimNumberString(numStr);

    const signPart = str.startsWith(SIGN) ? '-' : '';

    if (signPart !== '') {
        str = str.substring(SIGN.length, str.length);
    }

    const hasDecimal = isMatch(str, DEC_PARSING_PATTERN);

    if (!hasDecimal) {
        let digitsOnlyStr = str.replace(/[^\d]/g, '');

        if (!strict) {
            return signPart + digitsOnlyStr;
        }

        const numberPart = digitsOnlyStr === '' ? '' : String(Number(digitsOnlyStr));

        return (numberPart === '' || numberPart === '0' ? '' : signPart) + numberPart;
    }

    let [intPart, ...decParts] = str.split(new RegExp(DEC_PARSING_PATTERN)).map(t => t.replace(/[^\d]/g, ''));

    if (!strict) {
        return signPart + (intPart === '' ? '' : (intPart + '.')) + decParts.join('');
    }

    intPart = intPart.replace(/^0+/, '0');
    decParts = decParts.filter(t => t !== '');

    const decs = decParts.join('').replace(/0+$/, '');
    const numberPart = (intPart === '' ? '0' : Number(intPart)) + (decs.length > 0 ? '.' + decs : '');

    return (numberPart === '' || numberPart === '0' ? '' : signPart) + numberPart;
}


function numberToString(num) {
    let sign = '';
    let posNum = num;
    if (num.startsWith('-')) {
        sign = SIGN;
        posNum = num.substring(1, num.length);
    }

    const parts = posNum.split('.');

    if (parts.length === 1) {
        return sign + separateThousandsForIntPart(parts[0]);
    }

    return sign
        + separateThousandsForIntPart(parts[0])
        + DEC_SEP
        + separateThousandsForDecPart(parts[1]);
}

function separateThousandsForIntPart(numStr) {
    let str = '';

    let k = 0;

    for (let i = numStr.length - 1; i >= 0; i--) {
        if (k > 0 && k % 3 === 0) {
            str = THO_SEP + str;
        }

        k++;

        str = numStr.charAt(i) + str;
    }

    return str;
}

function separateThousandsForDecPart(numStr) {
    let str = '';

    for (let i = 0; i < numStr.length; i++) {
        if (i > 0 && i % 3 === 0) {
            str += '';
        }

        str += numStr.charAt(i);
    }

    return str;
}
